import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { useDeleteMember, useMyPermissions, useRoles, useUpdateMember, type TeamMember } from '@/hooks/use-settings'
import { useAuthStore } from '@/stores/auth-store'
import { ROLE_LABELS } from '@/pages/settings/settings-utils'
import { buildRoleOptions, memberRoleSelection, rolePayload } from './add-member-helpers'

/**
 * Sửa nhân viên người: họ tên, vai trò, đặt lại mật khẩu, khoá/mở. Chủ sở hữu chỉ đổi được tên.
 * Vai trò chọn từ danh sách vai trò THẬT của tổ chức (gửi `roleId`) — trước đây chỉ gửi cột `role` cũ nên
 * `roleId` (quyết định quyền thật) không đổi theo: đổi Quản trị viên → Quản lý mà quyền vẫn là Quản trị viên.
 */
export function EditHumanMemberDialog({ member, onClose }: { member: TeamMember | null; onClose: () => void }) {
  const { t } = useTranslation('team')
  const updateMember = useUpdateMember()
  const me = useAuthStore((s) => s.user)
  const perms = useMyPermissions()
  const canViewRoles = !!perms.data?.has('roles.view')
  const roles = useRoles(!!member && canViewRoles)
  const [form, setForm] = useState({ fullName: '', roleValue: '', password: '', isActive: true })
  const isOwner = member?.role === 'owner'

  const selection = useMemo(
    () =>
      member
        ? memberRoleSelection(member, buildRoleOptions(canViewRoles && !roles.isError ? roles.data : null, me?.role))
        : null,
    [member, canViewRoles, roles.isError, roles.data, me?.role],
  )
  const initialRole = selection?.value ?? ''
  const roleValue = form.roleValue || initialRole
  const roleOptions = selection?.options ?? []
  const systemOptions = roleOptions.filter((o) => o.isSystem)
  const customOptions = roleOptions.filter((o) => !o.isSystem)

  useEffect(() => {
    if (member) setForm({ fullName: member.fullName, roleValue: '', password: '', isActive: member.status === 'active' })
  }, [member])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!member) return
    try {
      await updateMember.mutateAsync({
        id: member.id,
        fullName: form.fullName.trim() || undefined,
        // Chỉ gửi vai trò khi thật sự đổi — giữ nguyên vai trò tuỳ chỉnh khi chỉ sửa tên / mật khẩu.
        ...(!isOwner && roleValue && roleValue !== initialRole ? rolePayload(roleValue) : {}),
        password: form.password.trim() || undefined,
        isActive: isOwner ? undefined : form.isActive,
      })
      toast.success(t('memberDialogs.updated'))
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('memberDialogs.editTitle')}</DialogTitle>
          <DialogDescription>{member?.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-name">{t('memberDialogs.fullName')}</Label>
            <Input
              id="edit-name"
              value={form.fullName}
              onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('memberDialogs.role')}</Label>
            {isOwner ? (
              <Select value="owner" disabled>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">{member?.roleName || ROLE_LABELS.owner}</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Select value={roleValue} onValueChange={(v) => setForm((s) => ({ ...s, roleValue: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder={roles.isLoading ? t('memberDialogs.loadingRoles') : t('memberDialogs.chooseRole')} />
                </SelectTrigger>
                <SelectContent>
                  {customOptions.length > 0 ? (
                    <>
                      <SelectGroup>
                        <SelectLabel>{t('memberDialogs.systemRoles')}</SelectLabel>
                        {systemOptions.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>{t('memberDialogs.customRoles')}</SelectLabel>
                        {customOptions.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </>
                  ) : (
                    roleOptions.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
            {isOwner && <p className="text-xs text-muted-foreground">{t('memberDialogs.ownerRoleLocked')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-pass">{t('memberDialogs.resetPassword')}</Label>
            <Input
              id="edit-pass"
              type="text"
              autoComplete="off"
              value={form.password}
              onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
              placeholder={t('memberDialogs.leaveBlank')}
              disabled={isOwner}
            />
          </div>
          {!isOwner && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label>{t('memberDialogs.active')}</Label>
                <p className="text-xs text-muted-foreground">{t('memberDialogs.activeHint')}</p>
              </div>
              <Switch checked={form.isActive} onCheckedChange={(v) => setForm((s) => ({ ...s, isActive: v }))} />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={updateMember.isPending}>
              {updateMember.isPending && <Loader2 className="animate-spin" />}
              {t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function DeleteHumanMemberDialog({ member, onClose }: { member: TeamMember | null; onClose: () => void }) {
  const { t } = useTranslation('team')
  const deleteMember = useDeleteMember()

  async function onDelete() {
    if (!member) return
    try {
      await deleteMember.mutateAsync(member.id)
      toast.success(t('memberDialogs.deleted'))
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('memberDialogs.deleteTitle')}</DialogTitle>
          <DialogDescription>
            <Trans
              t={t}
              i18nKey="memberDialogs.deleteConfirm"
              values={{ name: member?.fullName }}
              components={{ b: <span className="font-medium text-foreground" /> }}
            />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="destructive" onClick={onDelete} disabled={deleteMember.isPending}>
            {deleteMember.isPending && <Loader2 className="animate-spin" />}
            {t('common:actions.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
